import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Sister Properties – six closest Price Brothers communities to
 * The Residences at Bluhawk (from theresidencesatbluhawk.com/sisterproperties).
 *
 * Each card shows photo, name, full street address (linked to the sister
 * website), and a Visit Community CTA. Empty slots stay hidden on the live
 * site and show as placeholders in edit mode.
 */

// ─── Slot group definitions (one literal per community — schema extractor) ─

const community1Def: SlotGroupDefinition = {
  label: 'Community 1',
  description:
    'Closest sister community 1 of 6 to The Residences at Bluhawk. Leave empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto/s3//3/1124410/ks_overlandpark_wynnewoodfarms_p1099361_26_5_thumbnail.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Wynnewood Farms brick and gray-siding apartment buildings with white balcony railings, landscaped shrubs, and a green lawn under a blue sky',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Wynnewood Farms' },
    location: { label: 'Full Address', type: 'text', defaultValue: '5433 W 133rd Terrace, Overland Park, KS 66209' },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.wynnewoodfarms.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community2Def: SlotGroupDefinition = {
  label: 'Community 2',
  description:
    'Closest sister community 2 of 6 to The Residences at Bluhawk. Leave empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto/s3//3/1124099/ks_overlandpark_deercreek_p1099051_02_5_thumbnail.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Deer Creek Apartment Homes craftsman-style clubhouse entrance with a stone walkway, white pillars, and a red-and-white flower bed',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Deer Creek Apartment Homes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '12849 Metcalf Avenue, Overland Park, KS 66213' },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.deercreekaptskc.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community3Def: SlotGroupDefinition = {
  label: 'Community 3',
  description:
    'Closest sister community 3 of 6 to The Residences at Bluhawk. Leave empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto/s3//2/5458/ks_overlandpark_sorrentoatdeercreek_p1099140_4_03_1_communitythumbnai_1.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Sorrento at Deer Creek two-story tan apartment buildings with stone accents, arched windows, and balconies behind a manicured lawn',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Sorrento at Deer Creek Apartment Homes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '7700 W. 126th Terrace, Overland Park, KS 66213' },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.sorrentoapartmentskc.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community4Def: SlotGroupDefinition = {
  label: 'Community 4',
  description:
    'Closest sister community 4 of 6 to The Residences at Bluhawk. Leave empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto/s3//2/5458/6spr-amenities-4_thumbnail.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Stonepost Ranch outdoor swimming pool with clear blue water, lounge chairs, and a stone-accented clubhouse under a blue sky',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Stonepost Ranch Apartment Homes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '12801 W. 136th Street, Overland Park, KS 66221' },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.stonepostranchapts.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community5Def: SlotGroupDefinition = {
  label: 'Community 5',
  description:
    'Closest sister community 5 of 6 to The Residences at Bluhawk. Leave empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto/s3//2/5458/5spl-amenities-12_thumbnail.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Stonepost Lakeside resort-style pool with turquoise water, lounge chairs, and patio umbrellas framed by three-story tan apartment buildings',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Stonepost Lakeside Apartment Homes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '13810 S. Pflumm Road, Olathe, KS 66062' },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.stonepostlakesideapartments.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community6Def: SlotGroupDefinition = {
  label: 'Community 6',
  description:
    'Closest sister community 6 of 6 to The Residences at Bluhawk. Leave empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://resource.rentcafe.com/image/upload/q_auto,f_auto/s3//3/1124089/ks_overlandpark_creekside_p1099041_22_5_thumbnail(1).jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Creekside Apartment Homes red-brick clubhouse with an arched entryway, address plaque 11920, and landscaped flower beds along a curved walkway',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Creekside Apartment Homes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '11920 Oakmont Street, Overland Park, KS 66213' },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.creeksideaptskc.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isFilled(community: Record<string, string | null>): boolean {
  return hasText(community.name) || hasText(community.url) || hasText(community.image);
}

function CommunityCardBody({
  community,
  number,
  isEditing,
  filled,
}: {
  community: Record<string, string | null>;
  number: number;
  isEditing: boolean;
  filled: boolean;
}) {
  const href = (community.url || '').trim();
  const name = (community.name || '').trim();
  const location = (community.location || '').trim();
  const linkLabel = (community.label || 'Visit Community').trim();
  const target = community._target || '_blank';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

  return (
    <article
      className={`sister-card${filled ? '' : ' sister-card--empty'}`}
      aria-label={filled ? undefined : `Community ${number} — click to add details`}
    >
      <div className="sister-card-media">
        {hasText(community.image) ? (
          <VibesitesImage
            url={community.image || ''}
            alt={community.alt || name || `Community ${number}`}
            className="sister-card-image"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        ) : (
          <div className="sister-card-image-placeholder" aria-hidden="true" />
        )}
      </div>

      <div className="sister-card-body">
        <h3 className="sister-card-name">
          {name || (isEditing ? `Community ${number}` : '')}
        </h3>

        {location && href ? (
          <a
            className="sister-card-address"
            href={href}
            target={target}
            rel={rel}
            aria-label={`${linkLabel}: ${location}`}
          >
            {location}
          </a>
        ) : location ? (
          <p className="sister-card-address sister-card-address--plain">{location}</p>
        ) : isEditing && !filled ? (
          <p className="sister-card-address sister-card-hint">Add community details</p>
        ) : null}

        {href && name ? (
          <a
            className="sister-card-link"
            href={href}
            target={target}
            rel={rel}
          >
            {linkLabel}
          </a>
        ) : isEditing && !href && filled ? (
          <span className="sister-card-link sister-card-link--muted">Add sister website URL</span>
        ) : null}
      </div>
    </article>
  );
}

export function SisterProperties() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const editMode = useEditMode();
  const isEditing = Boolean(editMode?.isEditMode);

  const community1 = useSlotGroup('sisterproperties.community1', community1Def);
  const community2 = useSlotGroup('sisterproperties.community2', community2Def);
  const community3 = useSlotGroup('sisterproperties.community3', community3Def);
  const community4 = useSlotGroup('sisterproperties.community4', community4Def);
  const community5 = useSlotGroup('sisterproperties.community5', community5Def);
  const community6 = useSlotGroup('sisterproperties.community6', community6Def);

  return (
    <section ref={sectionRef} className="reveal-fade-up sister-properties">
      <div className="sister-properties-container">
        <div className="sister-properties-grid">
          {/*
            EditableSlotGroup groupPath must be a string literal for the schema
            linter — keep each wrapper inline rather than mapping a shared path.
          */}
          {(isEditing || isFilled(community1)) && (
            <EditableSlotGroup groupPath="sisterproperties.community1" definition={community1Def}>
              <CommunityCardBody
                community={community1}
                number={1}
                isEditing={isEditing}
                filled={isFilled(community1)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community2)) && (
            <EditableSlotGroup groupPath="sisterproperties.community2" definition={community2Def}>
              <CommunityCardBody
                community={community2}
                number={2}
                isEditing={isEditing}
                filled={isFilled(community2)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community3)) && (
            <EditableSlotGroup groupPath="sisterproperties.community3" definition={community3Def}>
              <CommunityCardBody
                community={community3}
                number={3}
                isEditing={isEditing}
                filled={isFilled(community3)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community4)) && (
            <EditableSlotGroup groupPath="sisterproperties.community4" definition={community4Def}>
              <CommunityCardBody
                community={community4}
                number={4}
                isEditing={isEditing}
                filled={isFilled(community4)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community5)) && (
            <EditableSlotGroup groupPath="sisterproperties.community5" definition={community5Def}>
              <CommunityCardBody
                community={community5}
                number={5}
                isEditing={isEditing}
                filled={isFilled(community5)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community6)) && (
            <EditableSlotGroup groupPath="sisterproperties.community6" definition={community6Def}>
              <CommunityCardBody
                community={community6}
                number={6}
                isEditing={isEditing}
                filled={isFilled(community6)}
              />
            </EditableSlotGroup>
          )}
        </div>

        {isEditing ? (
          <p className="sister-properties-editor-note">
            Showing the six closest sister communities to Bluhawk. Empty cards
            stay hidden on the live site.
          </p>
        ) : null}
      </div>

      <style>{`
        .sister-properties {
          padding: 3.5rem 0 4rem;
          background: #ffffff;
        }
        @media (min-width: 768px) {
          .sister-properties { padding: 5rem 0 5.5rem; }
        }

        .sister-properties-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .sister-properties-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sister-properties-container { padding: 0 2rem; } }

        .sister-properties-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .sister-properties-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .sister-properties-grid { grid-template-columns: repeat(3, 1fr); gap: 1.75rem; }
        }

        .sister-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .sister-card:hover {
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
          border-color: #dddde1;
        }

        .sister-card--empty {
          border-style: dashed;
          box-shadow: none;
          background: #fafafa;
        }
        .sister-card--empty:hover {
          box-shadow: none;
          border-color: var(--vibe-color-primary);
        }

        .sister-card-media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #e8e8ea;
          overflow: hidden;
        }

        .sister-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .sister-card-image-placeholder {
          width: 100%;
          height: 100%;
          background:
            linear-gradient(135deg, #ececef 0%, #e2e3e6 50%, #ececef 100%);
        }

        .sister-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          padding: 1.25rem 1.35rem 1.5rem;
          flex: 1;
        }

        .sister-card-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.3;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          margin: 0;
        }

        .sister-card-address {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: #000000;
          margin: 0;
          text-decoration: none;
        }
        a.sister-card-address:hover {
          text-decoration: underline;
        }

        .sister-card-hint {
          font-style: italic;
          color: var(--vibe-color-text-muted);
        }

        .sister-card-link {
          display: inline-flex;
          align-items: center;
          margin-top: auto;
          padding-top: 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: #000000;
          text-decoration: none;
        }
        .sister-card-link:hover {
          text-decoration: underline;
        }
        .sister-card-link--muted {
          color: var(--vibe-color-text-muted);
          font-style: italic;
          text-transform: none;
          letter-spacing: 0;
          font-family: var(--vibe-font-body);
          font-weight: 400;
          font-size: 0.85rem;
        }

        .sister-properties-editor-note {
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin: 1.75rem 0 0;
          font-style: italic;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SisterProperties',
  displayName: 'Sister Properties',
  tokens: {},
  slotGroups: {
    'sisterproperties.community1': community1Def,
    'sisterproperties.community2': community2Def,
    'sisterproperties.community3': community3Def,
    'sisterproperties.community4': community4Def,
    'sisterproperties.community5': community5Def,
    'sisterproperties.community6': community6Def,
  },
};

(SisterProperties as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SisterProperties;
